import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Mail, Search, Save, Loader2, CheckCircle, AlertCircle,
  Edit, X, RefreshCw, ChevronLeft, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import EmailNotificationToggles from './EmailNotificationToggles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClientEmailsConfig({ getAuthHeader }) {
  const [souscriptions, setSouscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editEmail, setEditEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  const fetchSouscriptions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '20' });
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(
        `${API}/admin/configuration/client-emails?${params.toString()}`,
        { headers: getAuthHeader() }
      );
      
      setSouscriptions(response.data.souscriptions || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch {
      toast.error('Erreur lors du chargement des emails clients');
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, getAuthHeader]);

  useEffect(() => {
    fetchSouscriptions();
  }, [fetchSouscriptions]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchSouscriptions();
  };

  const startEditing = (souscription) => {
    setEditingId(souscription.id || souscription.numero_police);
    setEditEmail(souscription.email || souscription.membre_email || '');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditEmail('');
  };

  const saveEmail = async (souscriptionId) => {
    if (!editEmail || !editEmail.includes('@')) {
      toast.error('Veuillez saisir un email valide');
      return;
    }

    setSavingEmail(true);
    try {
      await axios.put(
        `${API}/admin/configuration/client-emails/${souscriptionId}`,
        { email: editEmail },
        { headers: getAuthHeader() }
      );
      toast.success('Email client mis à jour');
      setEditingId(null);
      setEditEmail('');
      fetchSouscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingEmail(false);
    }
  };

  const getStatusBadge = (statut) => {
    const map = {
      'validee': { label: 'Validée', className: 'bg-green-100 text-green-700' },
      'en_attente': { label: 'En attente', className: 'bg-amber-100 text-amber-700' },
      'brouillon': { label: 'Brouillon', className: 'bg-gray-100 text-gray-700' },
      'annulee': { label: 'Annulée', className: 'bg-red-100 text-red-700' }
    };
    const config = map[statut] || { label: statut || 'N/A', className: 'bg-gray-100 text-gray-600' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  return (
    <div className="space-y-4">
      {/* V37.46 — Toggles globaux des rappels automatiques par email */}
      <EmailNotificationToggles getAuthHeader={getAuthHeader} />

      <Card>
        <CardHeader className="bg-blue-50">
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <Mail className="h-5 w-5" />
            Configuration des Emails Clients
          </CardTitle>
          <CardDescription className="text-blue-700">
            Renseignez ou modifiez manuellement les adresses email des clients pour les prélèvements SEPA et les notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {/* Barre de recherche */}
          <form onSubmit={handleSearch} className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                data-testid="client-emails-search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher par email, nom ou N° police..."
                className="pl-10"
              />
            </div>
            <Button type="submit" variant="outline" disabled={loading}>
              <Search className="h-4 w-4 mr-1" />
              Rechercher
            </Button>
            <Button type="button" variant="outline" onClick={() => { setSearchQuery(''); setPage(1); }} disabled={loading}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </form>

          {/* Compteur */}
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-gray-500">
              {total} souscription{total > 1 ? 's' : ''} trouvée{total > 1 ? 's' : ''}
            </p>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                <AlertCircle className="h-3 w-3 mr-1 text-amber-500" />
                {souscriptions.filter(s => !s.email && !s.membre_email).length} sans email
              </Badge>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : souscriptions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              Aucune souscription trouvée
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Entreprise / Client</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">N° Police</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Email</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Statut</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {souscriptions.map((s, idx) => {
                    const id = s.id || s.numero_police;
                    const currentEmail = s.email || s.membre_email || '';
                    const isEditing = editingId === id;

                    return (
                      <tr key={id || idx} className={`border-b hover:bg-gray-50 ${!currentEmail ? 'bg-amber-50/50' : ''}`}>
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-900">{s.raison_sociale || 'N/A'}</span>
                          {s.dispositif_nom && (
                            <p className="text-xs text-gray-500">{s.dispositif_nom}</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">{s.numero_police || 'N/A'}</code>
                        </td>
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <div className="flex items-center gap-1">
                              <Input
                                data-testid={`edit-email-${id}`}
                                type="email"
                                value={editEmail}
                                onChange={(e) => setEditEmail(e.target.value)}
                                className="h-8 text-sm"
                                placeholder="email@exemple.com"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveEmail(id);
                                  if (e.key === 'Escape') cancelEditing();
                                }}
                              />
                            </div>
                          ) : currentEmail ? (
                            <span className="text-gray-700 flex items-center gap-1">
                              <CheckCircle className="h-3 w-3 text-green-500" />
                              {currentEmail}
                            </span>
                          ) : (
                            <span className="text-amber-600 flex items-center gap-1 text-xs">
                              <AlertCircle className="h-3 w-3" />
                              Non renseigné
                            </span>
                          )}
                          {s.email_updated_at && !isEditing && (
                            <p className="text-[10px] text-gray-400 mt-0.5">
                              Modifié le {new Date(s.email_updated_at).toLocaleDateString('fr-FR')}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(s.statut)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {isEditing ? (
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                data-testid={`save-email-${id}`}
                                size="sm"
                                onClick={() => saveEmail(id)}
                                disabled={savingEmail || !editEmail}
                                className="h-7 bg-green-600 hover:bg-green-700"
                              >
                                {savingEmail ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                              </Button>
                              <Button size="sm" variant="outline" onClick={cancelEditing} className="h-7">
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              data-testid={`edit-btn-${id}`}
                              size="sm"
                              variant="outline"
                              onClick={() => startEditing(s)}
                              className="h-7"
                            >
                              <Edit className="h-3 w-3 mr-1" />
                              {currentEmail ? 'Modifier' : 'Ajouter'}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-500">
                Page {page} sur {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || loading}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
